import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchReservationOffer,
  offerRevisionAction,
  createOffer,
  requestOfferRevisionChanges,
  withdrawOfferRevision,
} from '../../api/services/listingService';

/**
 * Shared offer tracking panel — works for both buyer and owner views.
 *
 * Props:
 *   reservationId  — UUID of the ReservationRequest
 *   myRole         — 'buyer' | 'owner'
 *   listingType    — 'SALE' | 'RENT'
 *   onActioned     — optional callback fired after any successful action
 */

const OFFER_STATUS = {
  pending_response:  { text: 'در انتظار پاسخ',      color: '#f57c00', bg: '#fff3e0' },
  changes_requested: { text: 'تغییرات درخواست شده', color: '#1565c0', bg: '#e3f2fd' },
  accepted:          { text: 'پذیرفته شد ✓',         color: '#2e7d32', bg: '#e8f5e9' },
  rejected:          { text: 'رد شد',                 color: '#c62828', bg: '#ffebee' },
  withdrawn:         { text: 'پس گرفته شد',           color: '#757575', bg: '#f5f5f5' },
  expired:           { text: 'منقضی شد',              color: '#757575', bg: '#f5f5f5' },
  cancelled:         { text: 'لغو شد',                color: '#757575', bg: '#f5f5f5' },
  draft:             { text: 'پیش‌نویس',               color: '#9e9e9e', bg: '#f5f5f5' },
};

const REV_STATUS = {
  submitted:  { text: 'ارسال شده',    color: '#1565c0' },
  superseded: { text: 'جایگزین شده', color: '#9e9e9e' },
  accepted:   { text: 'پذیرفته شده', color: '#2e7d32' },
  rejected:   { text: 'رد شده',       color: '#c62828' },
  withdrawn:  { text: 'پس گرفته شده',color: '#757575' },
  draft:      { text: 'پیش‌نویس',     color: '#9e9e9e' },
};

const emptyForm = {
  asking_price: '', deposit: '', monthly_rent: '',
  delivery_date: '', buyer_conditions: [], owner_conditions: [], payment_phases: [],
};

function RevisionDetail({ rev }) {
  return (
    <div style={{ fontSize: 12 }}>
      {rev.asking_price && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>قیمت:</span><span>{Number(rev.asking_price).toLocaleString('fa-IR')} تومان</span>
        </div>
      )}
      {rev.deposit && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>ودیعه:</span><span>{Number(rev.deposit).toLocaleString('fa-IR')} تومان</span>
        </div>
      )}
      {rev.monthly_rent && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>اجاره ماهانه:</span><span>{Number(rev.monthly_rent).toLocaleString('fa-IR')} تومان</span>
        </div>
      )}
      {rev.delivery_date && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>تاریخ تحویل:</span><span>{rev.delivery_date}</span>
        </div>
      )}
      {rev.payment_phases?.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ color: '#555', marginBottom: 2 }}>مراحل پرداخت:</div>
          {rev.payment_phases.map((ph, i) => (
            <div key={i} style={{ paddingRight: 8 }}>
              {ph.label && `${ph.label} — `}{ph.date}: {Number(ph.amount).toLocaleString('fa-IR')} تومان
            </div>
          ))}
        </div>
      )}
      {rev.buyer_conditions?.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ color: '#1565c0', marginBottom: 2 }}>شرایط خریدار:</div>
          {rev.buyer_conditions.map((c, i) => <div key={i} style={{ paddingRight: 8 }}>• {c}</div>)}
        </div>
      )}
      {rev.owner_conditions?.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ color: '#2e7d32', marginBottom: 2 }}>شرایط مالک:</div>
          {rev.owner_conditions.map((c, i) => <div key={i} style={{ paddingRight: 8 }}>• {c}</div>)}
        </div>
      )}
    </div>
  );
}

function OfferForm({ isSale, form, setForm, onSubmit, onCancel, submitting, error, title }) {
  const [condInput, setCondInput] = useState('');
  const [phaseInput, setPhaseInput] = useState({ label: '', date: '', amount: '' });

  const addPhase = () => {
    if (!phaseInput.date || !phaseInput.amount) return;
    setForm(f => ({ ...f, payment_phases: [...f.payment_phases, { ...phaseInput }] }));
    setPhaseInput({ label: '', date: '', amount: '' });
  };

  const removePhase = (i) =>
    setForm(f => ({ ...f, payment_phases: f.payment_phases.filter((_, idx) => idx !== i) }));

  const addCondition = (field) => {
    if (!condInput.trim()) return;
    setForm(f => ({ ...f, [field]: [...f[field], condInput.trim()] }));
    setCondInput('');
  };

  const removeCondition = (field, i) =>
    setForm(f => ({ ...f, [field]: f[field].filter((_, idx) => idx !== i) }));

  const inputStyle = {
    width: '100%', padding: '6px 8px', borderRadius: 4,
    border: '1px solid #ddd', fontSize: 12, marginBottom: 6, boxSizing: 'border-box',
  };
  const btnStyle = (bg = '#1565c0') => ({
    padding: '5px 12px', borderRadius: 4, border: 'none', cursor: 'pointer',
    background: bg, color: '#fff', fontSize: 12,
  });

  return (
    <div style={{ marginTop: 10, padding: 10, background: '#f9f9f9', borderRadius: 6, border: '1px solid #e0e0e0' }} dir="rtl">
      {title && <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{title}</div>}

      {isSale ? (
        <input style={inputStyle} type="number" placeholder="قیمت پیشنهادی (تومان)"
          value={form.asking_price} onChange={e => setForm(f => ({ ...f, asking_price: e.target.value }))} />
      ) : (
        <>
          <input style={inputStyle} type="number" placeholder="ودیعه (تومان)"
            value={form.deposit} onChange={e => setForm(f => ({ ...f, deposit: e.target.value }))} />
          <input style={inputStyle} type="number" placeholder="اجاره ماهانه (تومان)"
            value={form.monthly_rent} onChange={e => setForm(f => ({ ...f, monthly_rent: e.target.value }))} />
        </>
      )}

      <input style={inputStyle} type="date" placeholder="تاریخ تحویل"
        value={form.delivery_date} onChange={e => setForm(f => ({ ...f, delivery_date: e.target.value }))} />

      {/* Payment phases */}
      <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>مراحل پرداخت (اختیاری):</div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
        <input style={{ ...inputStyle, flex: '1 1 60px', marginBottom: 0 }} placeholder="عنوان"
          value={phaseInput.label} onChange={e => setPhaseInput(p => ({ ...p, label: e.target.value }))} />
        <input style={{ ...inputStyle, flex: '1 1 90px', marginBottom: 0 }} type="date"
          value={phaseInput.date} onChange={e => setPhaseInput(p => ({ ...p, date: e.target.value }))} />
        <input style={{ ...inputStyle, flex: '1 1 70px', marginBottom: 0 }} type="number" placeholder="مبلغ"
          value={phaseInput.amount} onChange={e => setPhaseInput(p => ({ ...p, amount: e.target.value }))} />
        <button style={btnStyle()} onClick={addPhase}>+</button>
      </div>
      {form.payment_phases.map((ph, i) => (
        <div key={i} style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <span>{ph.label && `${ph.label} — `}{ph.date}: {Number(ph.amount).toLocaleString('fa-IR')} تومان</span>
          <button onClick={() => removePhase(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 12 }}>✕</button>
        </div>
      ))}

      {/* Buyer conditions */}
      <div style={{ fontSize: 11, color: '#1565c0', margin: '6px 0 2px' }}>شرایط خریدار:</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="افزودن شرط..."
          value={condInput} onChange={e => setCondInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCondition('buyer_conditions'); } }} />
        <button style={btnStyle()} onClick={() => addCondition('buyer_conditions')}>+</button>
      </div>
      {form.buyer_conditions.map((c, i) => (
        <div key={i} style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <span>• {c}</span>
          <button onClick={() => removeCondition('buyer_conditions', i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 12 }}>✕</button>
        </div>
      ))}

      {/* Owner conditions */}
      <div style={{ fontSize: 11, color: '#2e7d32', margin: '6px 0 2px' }}>شرایط مالک:</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="افزودن شرط..."
          value={condInput} onChange={e => setCondInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCondition('owner_conditions'); } }} />
        <button style={btnStyle('#2e7d32')} onClick={() => addCondition('owner_conditions')}>+</button>
      </div>
      {form.owner_conditions.map((c, i) => (
        <div key={i} style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <span>• {c}</span>
          <button onClick={() => removeCondition('owner_conditions', i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 12 }}>✕</button>
        </div>
      ))}

      {error && <div style={{ color: '#c62828', fontSize: 12, margin: '6px 0' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <button style={btnStyle('#1565c0')} onClick={onSubmit} disabled={submitting}>
          {submitting ? '...' : 'ارسال'}
        </button>
        <button style={btnStyle('#9e9e9e')} onClick={onCancel}>انصراف</button>
      </div>
    </div>
  );
}

export default function OfferPanel({ reservationId, myRole, listingType, onActioned }) {
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false); // false | 'create' | 'counter'
  const [showHistory, setShowHistory] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const isSale = listingType === 'SALE';

  const load = useCallback(async () => {
    try {
      const data = await fetchReservationOffer(reservationId);
      setOffer(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [reservationId]);

  useEffect(() => { load(); }, [load]);

  const seedForm = (rev) => setForm({
    asking_price: rev.asking_price || '',
    deposit: rev.deposit || '',
    monthly_rent: rev.monthly_rent || '',
    delivery_date: rev.delivery_date || '',
    buyer_conditions: [...(rev.buyer_conditions || [])],
    owner_conditions: [...(rev.owner_conditions || [])],
    payment_phases: [...(rev.payment_phases || [])],
  });

  const handleCreate = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        reservation: reservationId,
        delivery_date: form.delivery_date || null,
        buyer_conditions: form.buyer_conditions,
        owner_conditions: form.owner_conditions,
        payment_phases: form.payment_phases,
      };
      if (isSale) payload.asking_price = form.asking_price;
      else { payload.deposit = form.deposit; payload.monthly_rent = form.monthly_rent; }
      await createOffer(payload);
      setShowForm(false);
      setForm({ ...emptyForm });
      await load();
      if (onActioned) onActioned();
    } catch (e) {
      setError((e?.response?.data && Object.values(e.response.data).flat().join(' ')) || 'خطا در ارسال');
    } finally { setSubmitting(false); }
  };

  const handleCounter = async () => {
    if (!offer?.current_revision?.id) return;
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        delivery_date: form.delivery_date || null,
        buyer_conditions: form.buyer_conditions,
        owner_conditions: form.owner_conditions,
        payment_phases: form.payment_phases,
      };
      if (isSale) payload.asking_price = form.asking_price;
      else { payload.deposit = form.deposit; payload.monthly_rent = form.monthly_rent; }
      await requestOfferRevisionChanges(offer.current_revision.id, payload);
      setShowForm(false);
      await load();
      if (onActioned) onActioned();
    } catch (e) {
      setError((e?.response?.data && Object.values(e.response.data).flat().join(' ')) || 'خطا در ارسال');
    } finally { setSubmitting(false); }
  };

  const doAction = async (revisionId, action) => {
    setActionLoading(true);
    setError(null);
    try {
      if (action === 'withdraw') {
        await withdrawOfferRevision(revisionId);
      } else {
        await offerRevisionAction(revisionId, action, {});
      }
      await load();
      if (onActioned) onActioned();
    } catch (e) {
      setError((e?.response?.data?.detail) || 'خطا در انجام عملیات');
    } finally { setActionLoading(false); }
  };

  if (loading) return null;

  const revision = offer?.current_revision;
  const offerBadge = offer ? (OFFER_STATUS[offer.status] || { text: offer.status, color: '#333', bg: '#eee' }) : null;

  const revIsSubmitted = revision?.status === 'submitted';
  const isMyRevision = revision?.created_by_role === myRole;
  const isTheirRevision = revision?.created_by_role !== myRole;

  // Buyer: can send initial offer or re-send after terminal state
  const canCreateOffer = myRole === 'buyer' &&
    (!offer || ['rejected', 'withdrawn', 'expired', 'cancelled'].includes(offer.status));

  // Receiving party can respond to a submitted revision
  const canRespond = revIsSubmitted && isTheirRevision;

  // Sender can withdraw their own submitted revision
  const canWithdraw = revIsSubmitted && isMyRevision;

  // Buyer can accept the owner's counter (changes_requested sent by owner)
  const canAccept = canRespond;

  const btnStyle = (bg = '#1565c0', mt = 0) => ({
    padding: '5px 12px', borderRadius: 4, border: 'none', cursor: 'pointer',
    background: bg, color: '#fff', fontSize: 12, marginTop: mt,
  });

  const sectionStyle = {
    border: '1px solid #e0e0e0', borderRadius: 6,
    padding: '10px 12px', marginTop: 10, background: '#fff',
  };

  return (
    <div style={sectionStyle} dir="rtl">
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}> پیشنهاد معامله</div>

      {/* Offer status badge */}
      {offerBadge && (
        <div style={{
          display: 'inline-block', padding: '2px 10px', borderRadius: 10,
          fontSize: 12, background: offerBadge.bg, color: offerBadge.color, marginBottom: 8,
        }}>
          {offerBadge.text}
        </div>
      )}

      {/* Current revision details */}
      {revision && (
        <div style={{ borderRight: `3px solid ${REV_STATUS[revision.status]?.color || '#ccc'}`, paddingRight: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            ویرایش #{revision.revision_number} —{' '}
            <span style={{ color: REV_STATUS[revision.status]?.color }}>
              {REV_STATUS[revision.status]?.text || revision.status}
            </span>
            {' '}({revision.created_by_role === 'buyer' ? 'خریدار' : 'مالک'})
          </div>
          <RevisionDetail rev={revision} />
        </div>
      )}

      {/* No offer yet */}
      {!offer && myRole === 'buyer' && (
        <p style={{ fontSize: 12, color: '#9e9e9e', marginBottom: 8 }}>هنوز پیشنهادی ارسال نشده.</p>
      )}
      {!offer && myRole === 'owner' && (
        <p style={{ fontSize: 12, color: '#9e9e9e', marginBottom: 8 }}>خریدار هنوز پیشنهادی ارسال نکرده.</p>
      )}

      {/* Action buttons */}
      {!showForm && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {canAccept && (
            <button style={btnStyle('#2e7d32')} disabled={actionLoading}
              onClick={() => doAction(revision.id, 'accept')}>
              {actionLoading ? '...' : '✓ پذیرش'}
            </button>
          )}
          {canRespond && (
            <button style={btnStyle('#c62828')} disabled={actionLoading}
              onClick={() => doAction(revision.id, 'reject')}>
              {actionLoading ? '...' : '✕ رد'}
            </button>
          )}
          {canRespond && (
            <button style={btnStyle('#f57c00')} onClick={() => { seedForm(revision); setShowForm('counter'); }}>
              🔄 پیشنهاد متقابل
            </button>
          )}
          {canWithdraw && (
            <button style={btnStyle('#757575')} disabled={actionLoading}
              onClick={() => doAction(revision.id, 'withdraw')}>
              {actionLoading ? '...' : '↩ پس گرفتن'}
            </button>
          )}
          {canCreateOffer && (
            <button style={btnStyle('#1565c0')}
              onClick={() => { setForm({ ...emptyForm }); setShowForm('create'); }}>
              {offer ? '🔄 پیشنهاد جدید' : '📝 ارسال پیشنهاد'}
            </button>
          )}
        </div>
      )}

      {error && <div style={{ color: '#c62828', fontSize: 12, marginTop: 6 }}>{error}</div>}

      {/* Offer / counter form */}
      {showForm && (
        <OfferForm
          isSale={isSale}
          form={form}
          setForm={setForm}
          submitting={submitting}
          error={error}
          title={showForm === 'counter' ? '🔄 پیشنهاد متقابل' : '📝 پیشنهاد جدید'}
          onSubmit={showForm === 'counter' ? handleCounter : handleCreate}
          onCancel={() => { setShowForm(false); setError(null); }}
        />
      )}

      {/* Revision history */}
      {offer?.revisions?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowHistory(h => !h)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#1565c0', padding: 0 }}>
            {showHistory ? '▲ بستن تاریخچه' : `▼ تاریخچه ویرایش‌ها (${offer.revisions.length})`}
          </button>
          {showHistory && (
            <div style={{ marginTop: 8 }}>
              {[...offer.revisions].reverse().map(rev => (
                <div key={rev.id} style={{
                  borderRight: `3px solid ${REV_STATUS[rev.status]?.color || '#ccc'}`,
                  paddingRight: 8, marginBottom: 10,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    ویرایش #{rev.revision_number} —{' '}
                    <span style={{ color: REV_STATUS[rev.status]?.color }}>
                      {REV_STATUS[rev.status]?.text || rev.status}
                    </span>
                    {' '}({rev.created_by_role === 'buyer' ? 'خریدار' : 'مالک'})
                  </div>
                  <RevisionDetail rev={rev} />
                  {rev.submitted_at && (
                    <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 4 }}>
                      {new Date(rev.submitted_at).toLocaleDateString('fa-IR')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
